// socket.ts
import { io, Socket } from 'socket.io-client'
import Cookies from 'js-cookie'
import { API_HOST, TOKEN_KEY } from '@/shared/constants'

let socket: Socket | null = null
let socketKey: string | null = null
let refs = 0
let disconnectTimer: ReturnType<typeof setTimeout> | null = null
const DISCONNECT_GRACE_MS = 5_000

function getVisibilityState() {
  return typeof document === 'undefined' ? 'unknown' : document.visibilityState
}

function getOnlineState() {
  return typeof navigator === 'undefined' ? undefined : navigator.onLine
}

function sanitizeConnectionError(error: Error) {
  return `${error.name}: ${error.message}`
    .replace(/https?:\/\/\S+/gi, '[url]')
    .slice(0, 120)
}

export const getSocket = (companyId: number, userId: number): Socket => {
  const key = `${companyId}:${userId}`
  refs++
  if (disconnectTimer) {
    clearTimeout(disconnectTimer)
    disconnectTimer = null
  }

  if (!socket || socketKey !== key) {
    if (socketKey !== key) refs = 1
    // troca de company/user: derruba a instância antiga
    socket?.removeAllListeners()
    socket?.disconnect()

    const connectionStartedAt = Date.now()
    let hasConnected = false
    let disconnectedAt: number | undefined
    let disconnectReason: string | undefined
    let reconnectAttempts = 0
    let reconnectErrors = 0
    let connectErrors = 0
    let lastConnectError: string | undefined

    const nextSocket = io(API_HOST, {
      auth: { companyId, userId, token: Cookies.get(TOKEN_KEY) },
      autoConnect: true,

      // estabilidade: evita polling (reduz superfície de falha)
      transports: ['websocket'],

      // reconexão
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    })

    nextSocket.on('disconnect', (reason) => {
      disconnectedAt = Date.now()
      disconnectReason = reason
    })

    nextSocket.on('connect_error', (error) => {
      connectErrors++
      lastConnectError = sanitizeConnectionError(error)
    })

    nextSocket.io.on('reconnect_attempt', (attempt) => {
      reconnectAttempts = attempt
    })

    nextSocket.io.on('reconnect_error', () => {
      reconnectErrors++
    })

    nextSocket.on('connect', () => {
      const recoveredConnection =
        hasConnected || reconnectAttempts > 0 || connectErrors > 0

      if (recoveredConnection) {
        const reconnectedAt = Date.now()
        nextSocket.emit('client.connection.telemetry', {
          action: hasConnected ? 'reconnected' : 'initial_connection_recovered',
          disconnectedAt: new Date(
            disconnectedAt ?? connectionStartedAt
          ).toISOString(),
          reconnectedAt: new Date(reconnectedAt).toISOString(),
          offlineDurationMs:
            reconnectedAt - (disconnectedAt ?? connectionStartedAt),
          disconnectReason,
          reconnectAttempts,
          reconnectErrors,
          connectErrors,
          lastConnectError,
          visibilityState: getVisibilityState(),
          online: getOnlineState(),
        })
      }

      hasConnected = true
      disconnectedAt = undefined
      disconnectReason = undefined
      reconnectAttempts = 0
      reconnectErrors = 0
      connectErrors = 0
      lastConnectError = undefined
    })

    socket = nextSocket
    socketKey = key
  }

  return socket
}

export const disconnectSocket = () => {
  refs = Math.max(0, refs - 1)
  if (refs > 0) return

  if (disconnectTimer) clearTimeout(disconnectTimer)
  disconnectTimer = setTimeout(() => {
    disconnectTimer = null
    if (refs > 0 || !socket) return

    socket.removeAllListeners()
    socket.disconnect()
    socket = null
    socketKey = null
    refs = 0
  }, DISCONNECT_GRACE_MS)
}
